import React, { useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddCardIcon from '@mui/icons-material/AddCard';
import BlockIcon from '@mui/icons-material/Block';
import LinkIcon from '@mui/icons-material/Link';
import { useSelector } from 'react-redux';

import { useCorporateCardPayments } from '../../context/CorporateCardPaymentsContext';
import { ServicePaymentBadge } from '../ServicePaymentBadge';
import { RegisterPaymentModal } from '../RegisterPaymentModal';
import { adjustPaymentByService, annulPayment } from '../../services';
import { formatCard } from '../../constants';
import { CorporateCardPayment } from '../../types';
import { RootState } from 'store';
import { isAdminOrSuperAdmin } from 'features/auth/helpers';

interface Props {
	serviceId: string;
	saleOrderId: string;
	serviceCostUsd: number;
	providerName?: string;
}

interface PaymentRowProps {
	payment: CorporateCardPayment;
	canAnnul: boolean;
	onAnnulClick: (payment: CorporateCardPayment) => void;
}

const PaymentRow = ({ payment, canAnnul, onAnnulClick }: PaymentRowProps): JSX.Element => {
	const isAnnulled = payment.status === 'ANULADO';
	const isAdjustment = payment.adjustsPaymentId != null;

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 0.25,
				pl: isAdjustment ? 3 : 0,
				opacity: isAnnulled ? 0.6 : 1,
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
				{isAdjustment && (
					<Tooltip title={`Ajuste del pago #${payment.adjustsPaymentId}`}>
						<LinkIcon fontSize="inherit" sx={{ color: 'text.secondary', fontSize: '0.9rem' }} />
					</Tooltip>
				)}
				<Typography
					variant="body2"
					sx={{
						textDecoration: isAnnulled ? 'line-through' : 'none',
						color: isAnnulled ? 'text.disabled' : 'text.primary',
						fontWeight: 500,
					}}
				>
					USD {payment.amountUsd.toFixed(2)}
				</Typography>
				{payment.paymentCurrency && (
					<Typography variant="caption" color="text.secondary">
						({payment.paymentAmount} {payment.paymentCurrency})
					</Typography>
				)}
				<Chip
					label={formatCard(payment.card)}
					size="small"
					variant="outlined"
					sx={{ fontSize: '0.7rem', height: 20 }}
				/>
				<Typography variant="caption" color="text.secondary">
					{new Date(payment.paidAt).toLocaleDateString('es-AR')} — por {payment.registeredBy}
				</Typography>
				{isAnnulled && <Chip label="Anulado" size="small" color="default" sx={{ fontSize: '0.65rem', height: 18 }} />}
				{!isAnnulled && canAnnul && (
					<Tooltip title="Anular pago">
						<IconButton size="small" color="error" onClick={() => onAnnulClick(payment)} sx={{ ml: 'auto' }}>
							<BlockIcon sx={{ fontSize: '0.9rem' }} />
						</IconButton>
					</Tooltip>
				)}
			</Box>
			{isAnnulled && payment.annulReason && (
				<Typography variant="caption" color="text.disabled" sx={{ pl: isAdjustment ? 0 : 1 }}>
					Motivo: {payment.annulReason} {payment.annulledBy ? `— por ${payment.annulledBy}` : ''}
				</Typography>
			)}
			{payment.note && !isAnnulled && (
				<Typography variant="caption" color="text.secondary" sx={{ pl: isAdjustment ? 0 : 1 }}>
					{payment.note}
				</Typography>
			)}
		</Box>
	);
};

export const ServiceCorporateCardSection = ({
	serviceId,
	saleOrderId,
	serviceCostUsd,
	providerName,
}: Props): JSX.Element => {
	const auth = useSelector((state: RootState) => state.auth);
	const { getPaymentsForService, refresh, loading: ctxLoading } = useCorporateCardPayments();
	const [modalOpen, setModalOpen] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const [adjusting, setAdjusting] = useState(false);
	const [adjustError, setAdjustError] = useState<string | null>(null);
	const [annulTarget, setAnnulTarget] = useState<CorporateCardPayment | null>(null);
	const [annulReason, setAnnulReason] = useState('');
	const [annulling, setAnnulling] = useState(false);
	const [annulError, setAnnulError] = useState<string | null>(null);

	const canAnnul = isAdminOrSuperAdmin(auth.user);

	const handleAnnulOpen = (payment: CorporateCardPayment) => {
		setAnnulTarget(payment);
		setAnnulReason('');
		setAnnulError(null);
	};

	const handleAnnulClose = () => {
		if (annulling) return;
		setAnnulTarget(null);
		setAnnulReason('');
		setAnnulError(null);
	};

	const handleAnnulConfirm = async () => {
		if (!annulTarget || !annulReason.trim()) return;
		setAnnulling(true);
		setAnnulError(null);
		try {
			await annulPayment(annulTarget.id, annulReason.trim());
			await refresh();
			handleAnnulClose();
		} catch (err: unknown) {
			const axiosErr = err as { response?: { data?: { message?: string } } };
			setAnnulError(axiosErr.response?.data?.message ?? 'Error al anular el pago.');
		} finally {
			setAnnulling(false);
		}
	};

	const status = getPaymentsForService(serviceId);
	const hasPayments = status && status.payments.length > 0;
	const showPartialAlert = status && status.paid && !status.fullyPaid && status.difference < 0;
	const showOverpaidAlert = status && status.difference > 0;

	const canAdjust = (() => {
		if (!status || !showPartialAlert) return false;
		if (isAdminOrSuperAdmin(auth.user)) return true;
		const originalPmt = status.payments.find((p) => p.adjustsPaymentId === null && p.status === 'ACTIVO');
		return originalPmt?.registeredBy === auth.user?.username;
	})();

	const handleAdjust = async () => {
		setAdjusting(true);
		setAdjustError(null);
		try {
			await adjustPaymentByService(serviceId);
			await refresh();
		} catch (err: unknown) {
			const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
			if (axiosErr.response?.status === 409) {
				await refresh();
			} else if (axiosErr.response?.status === 503) {
				setAdjustError('Servicio no disponible. Intentá de nuevo.');
			} else {
				setAdjustError(axiosErr.response?.data?.message ?? 'Error al ajustar la diferencia.');
			}
		} finally {
			setAdjusting(false);
		}
	};

	const handlePaymentSuccess = async () => {
		await refresh();
	};

	return (
		<Box
			sx={{
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 1,
				p: 1.5,
				backgroundColor: '#fafafa',
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
				<CreditCardIcon fontSize="small" sx={{ color: 'text.secondary' }} />
				<Typography
					variant="caption"
					sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
				>
					Tarjeta corporativa
				</Typography>

				{ctxLoading ? (
					<CircularProgress size={14} />
				) : status ? (
					<ServicePaymentBadge status={status} />
				) : (
					<Typography variant="caption" color="text.secondary">
						Sin pagos registrados
					</Typography>
				)}

				<Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
					{hasPayments && (
						<IconButton size="small" onClick={() => setExpanded((v) => !v)} aria-label="Ver detalle de pagos">
							{expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
						</IconButton>
					)}

					<Button
						size="small"
						variant="outlined"
						startIcon={<AddCardIcon />}
						onClick={() => setModalOpen(true)}
						sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
					>
						Registrar pago
					</Button>
				</Box>
			</Box>

			{showPartialAlert && status && (
				<Alert
					severity="warning"
					sx={{ mt: 1, py: 0.5, fontSize: '0.8rem', alignItems: 'center' }}
					action={
						canAdjust ? (
							<Button
								size="small"
								color="warning"
								variant="contained"
								onClick={handleAdjust}
								disabled={adjusting}
								sx={{ fontSize: '0.7rem', py: 0.25, whiteSpace: 'nowrap' }}
							>
								{adjusting ? <CircularProgress size={14} /> : 'Ajustar diferencia'}
							</Button>
						) : (
							<Tooltip title="Pedile el ajuste a un administrador">
								<span>
									<Button size="small" disabled sx={{ fontSize: '0.7rem', py: 0.25 }}>
										Ajustar diferencia
									</Button>
								</span>
							</Tooltip>
						)
					}
				>
					Pagado USD {status.totalPaidUsd.toFixed(2)} de {status.serviceCostUsd.toFixed(2)} — diferencia USD{' '}
					{Math.abs(status.difference).toFixed(2)}
				</Alert>
			)}

			{showOverpaidAlert && status && (
				<Alert severity="warning" sx={{ mt: 1, py: 0.5, fontSize: '0.8rem' }}>
					Pagado de más: +USD {status.difference.toFixed(2)}. La corrección es manual (anular y recargar).
				</Alert>
			)}

			{adjustError && (
				<Alert severity="error" sx={{ mt: 1, py: 0.5, fontSize: '0.8rem' }}>
					{adjustError}
				</Alert>
			)}

			<Collapse in={expanded && hasPayments}>
				<Box sx={{ mt: 1.5 }}>
					<Divider sx={{ mb: 1 }} />
					<Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}>
						Detalle de pagos
					</Typography>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
						{status?.payments.map((payment) => (
							<PaymentRow key={payment.id} payment={payment} canAnnul={canAnnul} onAnnulClick={handleAnnulOpen} />
						))}
					</Box>
				</Box>
			</Collapse>

			<RegisterPaymentModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				serviceId={serviceId}
				saleOrderId={saleOrderId}
				serviceCostUsd={serviceCostUsd}
				providerName={providerName}
				onSuccess={handlePaymentSuccess}
				currentStatus={status}
			/>

			<Dialog open={!!annulTarget} onClose={handleAnnulClose} maxWidth="xs" fullWidth>
				<DialogTitle>Anular pago</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Vas a anular el pago de <strong>USD {annulTarget?.amountUsd.toFixed(2)}</strong> con{' '}
						{annulTarget && formatCard(annulTarget.card)}. Esta acción no se puede deshacer.
					</Typography>
					<TextField
						label="Motivo de anulación"
						fullWidth
						size="small"
						multiline
						rows={2}
						value={annulReason}
						onChange={(e) => setAnnulReason(e.target.value)}
						disabled={annulling}
						error={annulReason.trim() === '' && annulling}
						helperText="Requerido"
						autoFocus
					/>
					{annulError && (
						<Alert severity="error" sx={{ mt: 1.5 }}>
							{annulError}
						</Alert>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleAnnulClose} disabled={annulling}>
						Cancelar
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={handleAnnulConfirm}
						disabled={annulling || !annulReason.trim()}
					>
						{annulling ? <CircularProgress size={18} /> : 'Confirmar anulación'}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
